import React, {useCallback, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {WidthItem} from 'src/components/width-item';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';
import {UserType as UserTypes} from 'src/openapi/admin/models/UserType';

import Button from '@pb/kit-component-button';
import {ComboBox, ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateUserState} from 'src/openapi/admin/models/UpdateUserState';
import type {UserStatus} from 'src/openapi/admin/models/UserStatus';
import type {UserType} from 'src/openapi/admin/models/UserType';

type Props = {
    userIds: number[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateUserState>,
};

export const UpdateUserStateModal = ({userIds, onCloseModal, processAction}: Props) => {
    const {userStatuses} = useUserStatuses();
    const {userTypes} = useUserTypes();

    const [selectedType, setSelectedType] = useState<UserType | undefined>();
    const [selectedStatus, setSelectedStatus] = useState<UserStatus | undefined>();

    const changeType = useCallback((typeCode: string) => {
        setSelectedType(typeCode === 'default' ? undefined : typeCode as UserType);
    }, []);

    const changeStatus = useCallback((statusCode: string) => {
        setSelectedStatus(statusCode === 'default' ? undefined : statusCode as UserStatus);
    }, []);

    const updateState = useCallback(async (options?: ActionOptions) => {
        return await processAction({typeCode: selectedType, statusCode: selectedStatus, userIds}, options);
    }, [selectedType, selectedStatus, userIds, processAction]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v">
                    <WidthItem width="100%">
                        Update user type, status for selected users.
                    </WidthItem>
                    <WidthItem width="100%">
                        <Label>User Type</Label>
                        <ComboBox
                            value={selectedType || 'default'}
                            onChange={changeType}>
                            <DefaultItem value="default" caption="Keep Existing"/>
                            {userTypes
                                .filter(({code}) => code !== UserTypes.T_NA)
                                .map(({code, description}) => (
                                    <ComboboxItem key={code} caption={description} value={code}/>
                                ))}
                        </ComboBox>
                    </WidthItem>
                    <WidthItem width="100%">
                        <Label>User Status</Label>
                        <ComboBox
                            value={selectedStatus || 'default'}
                            onChange={changeStatus}>
                            <DefaultItem value="default" caption="Keep Existing"/>
                            {userStatuses
                                .filter(({code}) => code !== UserStatuses.EMPTY)
                                .map(({code, description}) => (
                                    <ComboboxItem key={code} caption={description} value={code}/>
                                ))}
                        </ComboBox>
                    </WidthItem>
                </Group>
            </DialogMain>
            <BulkActionFooter action="Update" onConfirm={updateState} disabled={!selectedStatus && !selectedType}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
